import {
  EReportColumnTypes,
  IReportDatasetLens,
  OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME,
  CalendarUnit,
  calendarUnitToFiscalYearFiscalPeriodColumnName,
  OrganizedByColumnType,
  ECalendarSemanticTypes,
  IReportConfigurationVisualizationQuery
} from '@opengov/reporting-types';
import {
  IReportColumn,
  IDimensionalReportColumn,
  mapColumnToFactory,
  EColumnDimensionType
} from '@opengov/reporting-types/src/reportColumns-immer';
import { getFiscalYearFiscalPeriodDimension } from '../../../../src/components/editor/sidepanel/columnEditor/columnEditorUtils';
import { IReportLocale } from '../../locale';
import { IOrganizedByDateOption } from '../../../reportExplorer/types';

export const calendarUnitToFiscalYearFiscalPeriodColumnType = {
  [CalendarUnit.Year]: OrganizedByColumnType.Date,
  [CalendarUnit.FiscalYear]: OrganizedByColumnType.Date,
  [CalendarUnit.Quarter]: OrganizedByColumnType.Text,
  [CalendarUnit.FiscalQuarter]: OrganizedByColumnType.Text,
  [CalendarUnit.FiscalPeriod]: OrganizedByColumnType.Date,
  [CalendarUnit.Month]: OrganizedByColumnType.Text
};

export function processOrganizedByColumn({
  column,
  selection,
  columnType,
  canUseFYFPColumn = false,
  lens
}: {
  column: IReportColumn;
  selection: CalendarUnit | string;
  columnType: OrganizedByColumnType;
  canUseFYFPColumn?: boolean;
  lens?: IReportDatasetLens | false;
}): {
  selection: CalendarUnit | string;
  newOrgByColumn: IReportColumn | IDimensionalReportColumn;
  columnType: OrganizedByColumnType;
} {
  if (canUseFYFPColumn && lens && column.name === OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME) {
    // Converts a fiscal year fiscal period column to a dimensional column
    const dimensionalColumn = mapColumnToFactory({
      ...column,
      columnType: EReportColumnTypes.dimensional,
      dimensionId: getFiscalYearFiscalPeriodDimension(lens).id,
      dimensionType: EColumnDimensionType.FISCAL_YEAR_FISCAL_PERIOD,
      joinColumnName: column.name,
      name: calendarUnitToFiscalYearFiscalPeriodColumnName[selection] as string
    });

    return {
      selection,
      newOrgByColumn: dimensionalColumn as IDimensionalReportColumn,
      columnType: calendarUnitToFiscalYearFiscalPeriodColumnType[selection] as OrganizedByColumnType
    };
  }

  return { selection, newOrgByColumn: column, columnType };
}

export function getDimensionalColumnSelectProps({
  column,
  selection,
  calendarUnitText
}: {
  column: IReportColumn;
  selection: CalendarUnit | string;
  calendarUnitText: IReportLocale['calendarUnits'];
}): { value: string; label: string } | null {
  if (column.columnType === EReportColumnTypes.dimensional && column.joinColumnName) {
    return {
      value: `${column.joinColumnName}: ${selection}`,
      label: `${column.displayName}: ${calendarUnitText[selection]}`
    };
  }
  return null;
}

function hasFiscalPeriodSemanticType(column: IReportColumn): boolean {
  return column.semanticTypes?.includes(ECalendarSemanticTypes.FISCAL_PERIOD) ?? false;
}

export function filterPredicateOfFYFPShowOptions({
  canUseFYFPColumn,
  showColumn
}: {
  canUseFYFPColumn: boolean;
  showColumn: IReportConfigurationVisualizationQuery['showColumn'];
}) {
  return (column: IReportColumn) => {
    const hasFPColumn = hasFiscalPeriodSemanticType(column);
    const showColumnIsFPColumn = hasFiscalPeriodSemanticType(showColumn);
    return (showColumnIsFPColumn && hasFPColumn) || !(canUseFYFPColumn && !showColumnIsFPColumn && hasFPColumn);
  };
}

export function getFYFPOrganizedByLabel({
  calendarUnitText,
  selection,
  dateColumns,
  showOnlyCalendarUnit = false
}: {
  calendarUnitText: IReportLocale['calendarUnits'];
  selection: CalendarUnit | string;
  dateColumns?: IOrganizedByDateOption[];
  showOnlyCalendarUnit?: boolean;
}) {
  const fiscalYearFiscalPeriodColumn = dateColumns?.find(
    (c) => c.columnName === OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME
  );

  if (fiscalYearFiscalPeriodColumn) {
    return showOnlyCalendarUnit
      ? calendarUnitText[selection]
      : `${fiscalYearFiscalPeriodColumn.columnDisplayName}: ${calendarUnitText[selection]}`;
  }

  return '';
}
